import { FC } from 'react';
import { Link, Redirect, useHistory, useLocation } from 'react-router-dom';
import { Center, Spacer } from '@chakra-ui/react';

import Button from 'components/_core/Button';
import Heading from 'components/_core/Heading';
import SunshineLoader from 'components/_core/SunshineLoader';
import Text from 'components/_core/Text';
import useToast from 'components/ToastProvider/useToast';

import NavigationHeader from '../../../../../components/NavigationHeader';
import { CreditCard } from '../../../../../hooks/useCreditCards';
import { useUpdateCreditCardStatusMutation } from '../../../hooks/useUpdateCreditCardStatusMutation';
import SidebarFooter from '../components/SidebarFooter';
import { useDeactivateVirtualCardConfirmQuery } from './hooks';
import locales from './locales';
import { LocationState } from './types';

interface DeactivateVirtualCardConfirmProps {
  creditCard: CreditCard;
}

const DeactivateVirtualCardConfirm: FC<DeactivateVirtualCardConfirmProps> = (
  props,
) => {
  const { creditCard } = props;
  const { creditCardId } = creditCard;

  const history = useHistory();
  const location = useLocation<LocationState | undefined>();
  const previousOperationId = location.state?.previousOperationId || '';

  const { showToast } = useToast();

  const {
    data,
    error: pageQueryError,
    loading: pageQueryLoading,
  } = useDeactivateVirtualCardConfirmQuery();

  const [updateCardStatus, { loading: updateCardStatusLoading }] =
    useUpdateCreditCardStatusMutation();

  if (pageQueryLoading) {
    return (
      <Center height="100%">
        <SunshineLoader />
      </Center>
    );
  }

  if (pageQueryError || !data) {
    showToast({
      message: locales.toastQueryError,
      type: 'error',
    });

    return <Redirect to={`/cards/${creditCardId}`} />;
  }

  const onConfirmClicked = async () => {
    await updateCardStatus({
      variables: { input: { creditCardId, status: 'DEACTIVATED' } },
    });

    showToast({
      message: locales.toastDeactivationSuccess,
      type: 'success',
    });

    history.push(`/cards/${creditCardId}`);
  };

  const { limit, used } = data.virtualCardQuotas;
  const hasReachedQuota = used >= limit;

  return (
    <>
      <NavigationHeader
        backTo={`/cards/${creditCardId}/${previousOperationId}`}
      />

      <Heading marginBottom="space-16">{locales.title}</Heading>

      <Text marginBottom="space-16">
        {locales.subtitleInfo}
        {hasReachedQuota ? (
          <>
            <br />
            {locales.subtitleNoMoreCardsInQuota}
          </>
        ) : null}
      </Text>

      <Spacer />

      <SidebarFooter>
        <Button
          isLoading={updateCardStatusLoading}
          onClick={onConfirmClicked}
          variant="danger"
          width="100%"
        >
          {locales.confirmButton}
        </Button>

        <Button
          as={Link}
          isDisabled={updateCardStatusLoading}
          to={`/cards/${creditCardId}`}
          variant="inline-secondary"
        >
          {locales.cancelButton}
        </Button>
      </SidebarFooter>
    </>
  );
};

export default DeactivateVirtualCardConfirm;
